/**
 * "This photo is being reviewed": what a gardener sees in place of a photo
 * of theirs that Grow's safety check flagged.
 *
 * The backend marks such a photo `under_review: true` (a plant's or a bed's
 * photo, a tray photo), a bed whose cover it is `cover_under_review: true`,
 * and a seed-packet scan of it `underReview: true`, and sends no links for
 * it. Rather than teach every screen that draws a photo, the API client
 * fills those links with one calm picture saying so, in the gardener's
 * language (fillUnderReview, called on every response), so a card, a tile,
 * the tray and the photo viewer all show it the same way. Sharing leaves
 * such photos out (isUnderReview).
 *
 * Who uses it: api/apiClient (fillUnderReview on every response), and the
 * share dialog and the season review (isUnderReview, to leave such photos
 * out). The words are sitewide.photoUnderReview.
 *
 * @module underReview
 */

import { t, getLanguage } from '@/shared/i18n/i18nSetup';

/** The link fields a photo is drawn from, in the shapes the API sends. */
const LINK_FIELDS = ['signed_photo_url', 'cardUrl', 'thumbUrl', 'signedUrl', 'url'] as const;

/** How deep a response is walked for flagged photos: a garden's plants' photos are about four levels down. */
const MAX_DEPTH = 8;

/** The picture, made once per language. */
const made = new Map<string, string>();

/**
 * Escapes text for an SVG text node.
 *
 * @param text - Words to put in the picture.
 * @returns The same words, safe inside XML.
 */
function xml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[c] as string));
}

/**
 * Splits a sentence into at most two lines of about half its length, at a space.
 *
 * @param text - The sentence.
 * @returns One or two lines.
 */
function twoLines(text: string): string[] {
  if (text.length <= 18) return [text];
  const middle = Math.floor(text.length / 2);
  const before = text.lastIndexOf(' ', middle);
  const after = text.indexOf(' ', middle);
  const at = before < 0 ? after : after < 0 ? before : (middle - before <= after - middle ? before : after);
  return at < 0 ? [text] : [text.slice(0, at), text.slice(at + 1)];
}

/**
 * The picture shown in place of a photo being reviewed: a quiet card with
 * an eye and the words, as an SVG data link (works in an img and a canvas).
 *
 * @returns A data: URL.
 */
export function underReviewImage(): string {
  const language = getLanguage();
  const cached = made.get(language);
  if (cached) return cached;
  const lines = twoLines(t('sitewide.photoUnderReview'));
  const text = lines.map((line, i) => `<text x="400" y="${360 + i * 44}" text-anchor="middle" font-family="system-ui, -apple-system, Segoe UI, sans-serif" font-size="34" fill="#5f6b63">${xml(line)}</text>`).join('');
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">'
    + '<rect width="800" height="600" fill="#eef1ec"/>'
    + '<g fill="none" stroke="#8a968d" stroke-width="10" stroke-linecap="round" stroke-linejoin="round">'
    + '<path d="M300 250c40-55 160-55 200 0c-40 55-160 55-200 0z"/><circle cx="400" cy="250" r="26"/></g>'
    + text + '</svg>';
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  made.set(language, url);
  return url;
}

/**
 * Whether a photo is being reviewed (and so is not offered for sharing).
 *
 * @param photo - Any photo the API sent.
 * @returns True when the backend marked it.
 */
export function isUnderReview(photo: unknown): boolean {
  return Boolean(photo && typeof photo === 'object' && (photo as { under_review?: unknown }).under_review === true);
}

/**
 * Puts the "being reviewed" picture into every link of every flagged photo
 * in a response, in place. Walks objects and arrays to MAX_DEPTH; anything
 * else is left as it is.
 *
 * @param data - A parsed response body.
 * @param depth - How deep this call is (internal).
 * @returns The same value, for chaining.
 */
export function fillUnderReview<T>(data: T, depth = 0): T {
  if (!data || typeof data !== 'object' || depth > MAX_DEPTH) return data;
  if (Array.isArray(data)) {
    for (const item of data) fillUnderReview(item, depth + 1);
    return data;
  }
  const record = data as Record<string, unknown>;
  if (record.under_review === true || record.underReview === true) {
    const picture = underReviewImage();
    for (const field of LINK_FIELDS) record[field] = picture;
  }
  if (record.cover_under_review === true) record.signed_photo_url = underReviewImage();
  for (const value of Object.values(record)) {
    if (value && typeof value === 'object') fillUnderReview(value, depth + 1);
  }
  return data;
}
